/**
 * Redux Slice for Prompt Management
 *
 * Manages the state for prompts including:
 * - Fetching prompts (defaults + user customizations)
 * - Saving prompt customizations
 * - Resetting prompts to defaults
 * - Testing prompts with sample input
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../../utils/authFetch';

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  // Prompts indexed by key
  byKey: {},

  // All prompt keys in order
  allKeys: [],

  // Available models
  models: [],

  // Permissions
  permissions: {
    canEdit: false,
    canEditDefaults: false
  },

  // Loading states
  loading: false,
  saving: false,
  testing: false,
  resetting: false,

  // Error state
  error: null,

  // Test results by prompt key
  testResults: {},

  // Last fetch timestamp
  lastFetched: null
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch all prompts (defaults merged with user customizations)
 */
export const fetchPrompts = createAsyncThunk(
  'prompts/fetchPrompts',
  async ({ category = null } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);

      const response = await authFetch(
        `/.netlify/functions/prompts-list${params.toString() ? `?${params}` : ''}`,
        {
          method: 'GET',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data;

    } catch (err) {
      console.error('[promptsSlice] fetchPrompts error:', err);
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Save prompt customization
 */
export const savePrompt = createAsyncThunk(
  'prompts/savePrompt',
  async (promptData, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/prompts-save', {
        method: 'POST',
        body: JSON.stringify(promptData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data;

    } catch (err) {
      console.error('[promptsSlice] savePrompt error:', err);
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Reset prompt to default (delete customization)
 */
export const resetPromptToDefault = createAsyncThunk(
  'prompts/resetPromptToDefault',
  async (promptKey, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/prompts-reset', {
        method: 'POST',
        body: JSON.stringify({ promptKey })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return { ...data, promptKey };

    } catch (err) {
      console.error('[promptsSlice] resetPromptToDefault error:', err);
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Test prompt with sample input
 */
export const testPrompt = createAsyncThunk(
  'prompts/testPrompt',
  async ({ promptKey, testVariables = {}, useCustomization = true }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/prompts-test', {
        method: 'POST',
        body: JSON.stringify({ promptKey, testVariables, useCustomization })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return { ...data, promptKey };

    } catch (err) {
      console.error('[promptsSlice] testPrompt error:', err);
      return rejectWithValue({ promptKey, error: err.message });
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

const promptsSlice = createSlice({
  name: 'prompts',
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Clear test results
    clearTestResults: (state) => {
      state.testResults = {};
    },

    // Clear test result for specific prompt
    clearPromptTestResult: (state, action) => {
      const { promptKey } = action.payload;
      delete state.testResults[promptKey];
    },

    // Update prompt locally (for optimistic updates)
    updatePromptLocal: (state, action) => {
      const prompt = action.payload;
      if (prompt?.promptKey && state.byKey[prompt.promptKey]) {
        state.byKey[prompt.promptKey] = {
          ...state.byKey[prompt.promptKey],
          ...prompt
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // ============ Fetch Prompts ============
      .addCase(fetchPrompts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrompts.fulfilled, (state, action) => {
        state.loading = false;
        const { prompts, models, permissions, timestamp } = action.payload;

        // Index prompts by key
        state.byKey = {};
        state.allKeys = [];

        for (const prompt of prompts) {
          state.byKey[prompt.promptKey] = prompt;
          state.allKeys.push(prompt.promptKey);
        }

        state.models = models || [];
        state.permissions = permissions || { canEdit: false, canEditDefaults: false };
        state.lastFetched = timestamp;
      })
      .addCase(fetchPrompts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch prompts';
      })

      // ============ Save Prompt ============
      .addCase(savePrompt.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(savePrompt.fulfilled, (state, action) => {
        state.saving = false;
        const { prompt } = action.payload;

        if (prompt?.promptKey) {
          // Update the prompt in state
          state.byKey[prompt.promptKey] = {
            ...state.byKey[prompt.promptKey],
            ...prompt,
            resolvedFrom: 'user_customization'
          };
        }
      })
      .addCase(savePrompt.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || 'Failed to save prompt';
      })

      // ============ Reset Prompt ============
      .addCase(resetPromptToDefault.pending, (state) => {
        state.resetting = true;
        state.error = null;
      })
      .addCase(resetPromptToDefault.fulfilled, (state, action) => {
        state.resetting = false;
        const { prompt, promptKey } = action.payload;

        if (promptKey && prompt) {
          // Update with default prompt
          state.byKey[promptKey] = {
            ...state.byKey[promptKey],
            ...prompt,
            resolvedFrom: prompt.resolvedFrom || 'database_default'
          };
        }
      })
      .addCase(resetPromptToDefault.rejected, (state, action) => {
        state.resetting = false;
        state.error = action.payload || 'Failed to reset prompt';
      })

      // ============ Test Prompt ============
      .addCase(testPrompt.pending, (state, action) => {
        state.testing = true;
        state.error = null;
        // Clear previous test result for this prompt
        const promptKey = action.meta.arg.promptKey;
        if (promptKey) {
          state.testResults[promptKey] = {
            loading: true,
            error: null,
            result: null
          };
        }
      })
      .addCase(testPrompt.fulfilled, (state, action) => {
        state.testing = false;
        const { promptKey, result, metrics, testInput, timestamp } = action.payload;

        if (promptKey) {
          state.testResults[promptKey] = {
            loading: false,
            error: null,
            result,
            metrics,
            testInput,
            timestamp
          };
        }
      })
      .addCase(testPrompt.rejected, (state, action) => {
        state.testing = false;
        const { promptKey, error } = action.payload || {};

        if (promptKey) {
          state.testResults[promptKey] = {
            loading: false,
            error: error || 'Test failed',
            result: null
          };
        } else {
          state.error = action.payload || 'Failed to test prompt';
        }
      });
  }
});

// ============================================================================
// Actions
// ============================================================================

export const {
  clearError,
  clearTestResults,
  clearPromptTestResult,
  updatePromptLocal
} = promptsSlice.actions;

// ============================================================================
// Selectors
// ============================================================================

/**
 * Select all prompts
 */
export const selectAllPrompts = (state) => {
  const allKeys = state.prompts?.allKeys || [];
  const byKey = state.prompts?.byKey || {};
  return allKeys.map(key => byKey[key]).filter(Boolean);
};

/**
 * Select prompts by category
 */
export const selectPromptsByCategory = (state, category) => {
  const allPrompts = selectAllPrompts(state);
  return allPrompts.filter(prompt => prompt.promptCategory === category);
};

/**
 * Select Builder prompts
 */
export const selectBuilderPrompts = (state) => selectPromptsByCategory(state, 'builder');

/**
 * Select Explorer prompts
 */
export const selectExplorerPrompts = (state) => selectPromptsByCategory(state, 'explorer');

/**
 * Select Security prompts (prompts with promptFlow === 'security')
 */
export const selectSecurityPrompts = (state) => {
  const allPrompts = selectAllPrompts(state);
  return allPrompts.filter(prompt => prompt.promptFlow === 'security');
};

/**
 * Select prompt by key
 */
export const selectPromptByKey = (state, promptKey) => {
  return state.prompts?.byKey?.[promptKey] || null;
};

/**
 * Select customized prompts only
 */
export const selectCustomizedPrompts = (state) => {
  const allPrompts = selectAllPrompts(state);
  return allPrompts.filter(prompt => prompt.resolvedFrom === 'user_customization');
};

/**
 * Select available models
 */
export const selectAvailableModels = (state) => {
  return state.prompts?.models || [];
};

/**
 * Select active models (not coming_soon or enterprise)
 */
export const selectActiveModels = (state) => {
  const models = selectAvailableModels(state);
  return models.filter(m => m.status === 'active');
};

/**
 * Select permissions
 */
export const selectPromptPermissions = (state) => {
  return state.prompts?.permissions || { canEdit: false, canEditDefaults: false };
};

/**
 * Select loading state
 */
export const selectPromptsLoading = (state) => state.prompts?.loading || false;

/**
 * Select saving state
 */
export const selectPromptsSaving = (state) => state.prompts?.saving || false;

/**
 * Select testing state
 */
export const selectPromptsTesting = (state) => state.prompts?.testing || false;

/**
 * Select error
 */
export const selectPromptsError = (state) => state.prompts?.error || null;

/**
 * Select test result for a prompt
 */
export const selectPromptTestResult = (state, promptKey) => {
  return state.prompts?.testResults?.[promptKey] || null;
};

/**
 * Select prompts grouped by flow
 */
export const selectPromptsGroupedByFlow = (state) => {
  const allPrompts = selectAllPrompts(state);
  const grouped = {};

  for (const prompt of allPrompts) {
    const flow = prompt.promptFlow || 'other';
    if (!grouped[flow]) {
      grouped[flow] = [];
    }
    grouped[flow].push(prompt);
  }

  return grouped;
};

// ============================================================================
// Export
// ============================================================================

export default promptsSlice.reducer;
